const PLAN_LEVELS = {
  free: 0,
  starter: 1,
  growth: 2,
  business: 3,
  premium: 4,
  agency: 5,
} as const

type PlanName = keyof typeof PLAN_LEVELS

/**
 * Checks if a user needs to upgrade their plan to access a feature
 * @param currentPlan - The user's current plan
 * @param planRequired - The minimum plan required for the feature
 * @returns true if user needs to upgrade, false if they have access
 */
export function shouldShowUpgradeBadge(
  currentPlan?: string,
  planRequired?: string,
): boolean {
  // If no plan is required, don't show upgrade badge
  if (!planRequired) return false

  // If current plan is missing but a plan is required, show upgrade badge
  if (!currentPlan) return true

  // Don't show badge for AppSumo users
  if (currentPlan.toLowerCase().includes("appsumo")) return false

  // Normalize the plan names to lowercase and handle potential undefined cases
  const currentPlanLower = currentPlan?.toLowerCase() || ""
  const requiredPlanLower = planRequired?.toLowerCase() || ""

  // Check if plans are valid
  if (
    !(currentPlanLower in PLAN_LEVELS) ||
    !(requiredPlanLower in PLAN_LEVELS)
  ) {
    console.warn("Invalid plan level:", { currentPlan, planRequired })
    // If we can't determine the plans, be restrictive and show upgrade badge
    return true
  }

  const currentLevel = PLAN_LEVELS[currentPlanLower as PlanName]
  const requiredLevel = PLAN_LEVELS[requiredPlanLower as PlanName]

  // Only show badge if required level is higher than current level
  return requiredLevel > currentLevel
}

/**
 * Checks if a user has access to a feature based on their plan
 * @param currentPlan - The user's current plan
 * @param planRequired - The minimum plan required for the feature
 * @returns true if user has access, false if they need to upgrade
 */
export function hasFeatureAccess(
  currentPlan?: string,
  planRequired?: string,
): boolean {
  return !shouldShowUpgradeBadge(currentPlan, planRequired)
}

/**
 * Gets the numeric level of a plan
 * @param planName - The plan name
 * @returns The numeric level of the plan, or -1 if invalid
 */
export function getPlanLevel(planName?: string): number {
  if (!planName) return -1

  const planLower = planName.toLowerCase()
  if (!(planLower in PLAN_LEVELS)) return -1

  return PLAN_LEVELS[planLower as PlanName]
}

/**
 * Checks if one plan is higher than another
 * @param planA - First plan to compare
 * @param planB - Second plan to compare
 * @returns true if planA is higher than planB
 */
export function isPlanHigher(planA?: string, planB?: string): boolean {
  const levelA = getPlanLevel(planA)
  const levelB = getPlanLevel(planB)

  if (levelA === -1 || levelB === -1) return false

  return levelA > levelB
}
